import React, { useState, useEffect, SyntheticEvent } from 'react'
import { Classes, InputGroup, Intent, FormGroup } from '@blueprintjs/core'
import { Flex, LocationSelect, Confirmation, Button, Details, Toaster } from '@ui'
import api, { FormErrors, INeighborhoodDetails, BMDialogFloor } from '@api'
import { omit } from 'lodash'
import { ApolloError } from 'apollo-boost'
import { Permissions, PermissionsType, IPermissionsOnFloor } from '@permissions'

export interface FormNeighborhood {
  id?: string
  name: string
  maxEmployees: string
  floorId: string
}

type NeighborhoodFormErrors = FormErrors<FormNeighborhood>

interface RequiredField {
  key: 'name' | 'floorId'
  label: string
}

const requiredFields: RequiredField[] = [
  { key: 'name', label: 'Name' },
  { key: 'floorId', label: 'Location' },
]

function isValid(neighborhood: FormNeighborhood) {
  let valid = true
  requiredFields.forEach(({ key }) => {
    if (valid) {
      valid = !!neighborhood[key]
    }
  })

  return valid
}

const initNeighborhood: FormNeighborhood = {
  name: '',
  maxEmployees: '',
  floorId: '',
}

export default function NeighborhoodForm({
  neighborhood: initState = initNeighborhood,
  floorId,
  dirty,
  setDirty,
  onSubmit,
  onCancel,
}: {
  neighborhood?: FormNeighborhood
  setDirty: (value: boolean) => void
  dirty: boolean
  onSubmit: (newNeighborhood: INeighborhoodDetails) => void
  onCancel: () => void
  floorId?: string
}) {
  const [errors, setErrors] = useState<NeighborhoodFormErrors>({})
  const [neighborhood, setNeighborhood] = useState<FormNeighborhood>(initState)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setNeighborhood((neighborhood) => ({
      ...neighborhood,
      floorId: floorId ? floorId : '',
    }))
  }, [floorId])

  const onTextInputChange = ({
    currentTarget: { name, value },
  }: SyntheticEvent<HTMLInputElement>) => {
    setDirty(true)
    setErrors(omit(errors, name))
    setNeighborhood({
      ...neighborhood,
      [name]: value,
    })
  }

  const handleErrors = ({ kind, graphQLErrors }: any) => {
    setLoading(false)

    if (graphQLErrors && graphQLErrors.length) {
      const newErrors: NeighborhoodFormErrors = graphQLErrors.reduce(
        (result: NeighborhoodFormErrors, { invalidArguments }: any) => {
          return Object.assign(
            result,
            Object.entries(invalidArguments).reduce(
              (result, [key, value]) => ({
                ...result,
                [key]: value,
              }),
              {}
            )
          )
        },
        {}
      )

      setErrors(newErrors)
    }
  }

  const highlightErrors = () => {
    const foundErrors: NeighborhoodFormErrors = {}
    requiredFields.forEach((field) => {
      if (!neighborhood[field.key]) {
        foundErrors[field.key] = `${field.label} is required`
      }
    })
    if (Object.keys(foundErrors).length) {
      setErrors((prevState) => ({ ...prevState, ...foundErrors }))
    }
  }

  const columns = [
    [
      {
        label: 'Name',
        required: true,
        formEl: (
          <FormGroup helperText={errors.name} intent={errors.name ? Intent.DANGER : undefined}>
            <InputGroup
              autoFocus
              placeholder="Name of new neighborhood"
              name="name"
              intent={errors.name ? Intent.DANGER : undefined}
              value={neighborhood.name}
              onChange={onTextInputChange}
            />
          </FormGroup>
        ),
      },
      {
        label: 'Location',
        required: true,
        formEl: (
          <FormGroup
            helperText={errors.floorId}
            intent={errors.floorId ? Intent.DANGER : undefined}
          >
            <LocationSelect
              buttonProps={{
                ghost: true,
              }}
              mode={LocationSelect.Mode.SINGLE_FLOOR}
              value={
                neighborhood.id
                  ? neighborhood.floorId
                  : floorId
                  ? floorId
                  : neighborhood.floorId
                  ? neighborhood.floorId
                  : null
              }
              onChange={(newLocation) => {
                if (!floorId) {
                  setNeighborhood((nh) => ({
                    ...nh,
                    floorId: newLocation.id,
                  }))

                  setErrors({})
                }
              }}
            />
          </FormGroup>
        ),
      },
      {
        label: 'Max Employees',
        formEl: (
          <FormGroup
            helperText={errors.maxEmployees}
            intent={errors.maxEmployees ? Intent.DANGER : undefined}
          >
            <InputGroup
              placeholder=""
              name="maxEmployees"
              intent={errors.maxEmployees ? Intent.DANGER : undefined}
              value={neighborhood.maxEmployees}
              onChange={onTextInputChange}
            />
          </FormGroup>
        ),
      },
    ],
  ]

  return (
    <Permissions type={PermissionsType.FLOOR} on={{ id: neighborhood.floorId } as BMDialogFloor}>
      {(permissions) => {
        const { canCreateNeighborhood } = permissions as IPermissionsOnFloor

        return (
          <div className={Classes.DIALOG_BODY}>
            <Details
              onSubmit={(e: SyntheticEvent<HTMLFormElement>) => {
                e.preventDefault()
                if (!isValid(neighborhood)) {
                  highlightErrors()
                  return
                }

                if (!canCreateNeighborhood) {
                  setErrors((prev) => ({
                    ...prev,
                    floorId: 'You have not permissions for create neighborhood on this floor.',
                  }))
                  return
                }

                setLoading(true)

                if (neighborhood.id) {
                  api.neighborhood
                    .update({
                      id: neighborhood.id,
                      maxEmployees: parseInt(neighborhood.maxEmployees),
                    })
                    .then((updatedNeigborhood) => onSubmit(updatedNeigborhood))
                    .then(() =>
                      Toaster.success({
                        message: 'Neighborhood updated',
                      })
                    )
                    .catch((error: ApolloError) => {
                      handleErrors(error)
                      Toaster.fail({
                        error,
                      })
                    })
                } else {
                  api.neighborhood
                    .create({
                      name: neighborhood.name,
                      floorId: neighborhood.floorId,
                      capacity: neighborhood.maxEmployees ? neighborhood.maxEmployees : undefined,
                    })
                    .then((updatedNeigborhood) => onSubmit(updatedNeigborhood))
                    .then(() =>
                      Toaster.success({
                        message: 'Neighborhood Created',
                      })
                    )
                    .catch((error: ApolloError) => {
                      handleErrors(error)
                      Toaster.fail({
                        error,
                      })
                    })
                }
              }}
            >
              <Details.Columns>
                {columns.map((rows, i) => (
                  <Details.Column key={i} style={i === 0 ? { width: 582 } : {}}>
                    {rows.map(({ label, required, formEl }) => (
                      <Details.Row key={label} label={label} required={required}>
                        {formEl}
                      </Details.Row>
                    ))}
                  </Details.Column>
                ))}
              </Details.Columns>
              <Details.Footer>
                <Flex>
                  <div style={{ marginRight: 8 }}>
                    {dirty ? (
                      <Confirmation text="Discard unsaved changes?" onConfirm={onCancel}>
                        <Button type="button" text="Cancel" />
                      </Confirmation>
                    ) : (
                      <Button type="button" text="Cancel" onClick={onCancel} />
                    )}
                  </div>
                  <Button
                    type="submit"
                    disabled={!dirty}
                    loading={loading}
                    intent={Intent.PRIMARY}
                    text={neighborhood.id ? 'Update' : 'Create'}
                  />
                </Flex>
              </Details.Footer>
            </Details>
          </div>
        )
      }}
    </Permissions>
  )
}
